import { AxiosResponse } from "axios";
import catalog from "@granular/fabric-catalog";

import { axiosInstance, computeHeaders } from "./axios";
import {
  granularAxiosInstance,
  getIsAxiosError,
  getAuthToken,
} from "./granularAxios";
import { ensureError } from "./util";
import { Operation, Member, CreatorRole } from "../models";
import { UpdatePermissionsRequest } from "../models/permissions";
import { getRoleFromPermissions } from "../helpers";
import {
  refinedOperation_NOT_MEMOIZED,
  refinedListOfOperations,
  refinedListOfOperations_NOT_MEMOIZED,
  sortedListOfOperations,
} from "./helpers/refinements";
import {
  OperationResponse,
  OperationsNetworkResponse,
  DataForCreateOperationNetworkRequest,
  DataForUpdateOperationNetworkRequest,
  DataForUpdateOperationSettingsNetworkRequest,
  UpdateOperationNetworkResponse,
  UpdateOperationSettingsNetworkResponse,
  OperationResponseV4,
} from "../models/operations";
import {
  MemberResponse,
  MembersResponse,
  MembersNetworkResponse,
} from "../models/members";

const CURRENT_OPERATION_KEY_IN_LOCAL_STORAGE = "granular-current-operation-id";

/**
 * The number of `Operation` entities we procure in a call to the service. This
 * is *also* the maximum number of results we retrieve in our search endpoint.
 *
 * Let's not go full Enterprise™ and keep this a *reasonable* number (based and
 * updated upon middle-school statistics about the average number of operations
 * per user and NOT gut-feelings or a CSA's panicked/angry Slack message) that
 * doesn't cause performance issues.
 */
export const OPERATIONS_PAGE_SIZE = 250;

/**
 * List of Operations
 *
 * TODO: Recursive calls to fetch *all* entities are a temporary measure
 * to not break existing things.
 *
 * Couple of considerations here:
 *
 * 1) The *component* that paginates should make requests (via hooks or
 *    Redux actions) *on demand* for the next 'page' of results. If you
 *    have 9 'pages' of operations and the user only needs 4, you've
 *    made 5 unnecessary calls.
 *
 * 2) *Upstream APIs must be held responsible* for proper (a) sorting
 *    (b) searching and (c) pagination. It is the most ludicrous thing
 *    to burn CPU cycles, memory, and bandwidth doing all these things
 *    right in the client's ****ing browser. With all the talk around
 *    'optimizing' the frontend experience, this is the lowest-hanging
 *    fruit #FIXUPSTREAMFIRST
 *
 */
const getListOfOperationsResponse = async (
  shouldLimitTo1Page: boolean,
  catalogFlag?: boolean,
): Promise<OperationsNetworkResponse> => {
  let response;
  if (catalogFlag) {
    response = await catalog.roles.getOperations(
      {
        settings: true,
        user_perms: true,
        limit: OPERATIONS_PAGE_SIZE,
      },
      { getAuthToken },
    );
  } else {
    response = await granularAxiosInstance.get<OperationsNetworkResponse>(
      `/api/roles-svc/v2/operation?settings=true&user_perms=true&limit=${OPERATIONS_PAGE_SIZE}`,
    );
  }
  let loadedItems: OperationResponse[] = [];
  const promises = [];
  // Check the results of the first response
  if (response.data.operations?.length) {
    loadedItems = loadedItems.concat(response.data.operations);

    // If there are more objects, create concurrent requests to get the remaining operations.
    if (
      !shouldLimitTo1Page &&
      response.data._metadata.count > OPERATIONS_PAGE_SIZE
    ) {
      for (
        let offset = OPERATIONS_PAGE_SIZE;
        offset < response.data._metadata.count;
        offset += OPERATIONS_PAGE_SIZE
      ) {
        if (catalogFlag) {
          promises.push(
            catalog.roles.getOperations(
              {
                settings: true,
                user_perms: true,
                limit: OPERATIONS_PAGE_SIZE,
              },
              { getAuthToken },
            ),
          );
        } else {
          promises.push(
            granularAxiosInstance.get<OperationsNetworkResponse>(
              `/api/roles-svc/v2/operation?settings=true&user_perms=true&limit=${OPERATIONS_PAGE_SIZE}&offset=${offset}`,
            ),
          );
        }
      }
    }
  }
  const results = await Promise.all(promises);
  results.forEach((response) => {
    loadedItems = loadedItems.concat(response.data.operations);
  });

  return {
    _metadata: response.data._metadata,
    operations: loadedItems,
  };
};
const getListOfOperations = async (
  shouldLimitTo1Page: boolean,
  catalogFlag?: boolean,
): Promise<Operation[]> => {
  const { operations } = await getListOfOperationsResponse(
    shouldLimitTo1Page,
    catalogFlag,
  );

  return sortedListOfOperations(refinedListOfOperations(operations));
};

/**
 * A Single Operation
 */

export const getOperationResponse = (
  operationId: string,
  catalogFlag?: boolean,
): Promise<AxiosResponse<OperationResponse>> => {
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    return catalog.roles.getOperation(
      operationId,
      { settings: true, user_perms: true },
      catalogConfig,
    );
  } else {
    return granularAxiosInstance.get(
      `/api/roles-svc/v2/operation/${operationId}?settings=true&user_perms=true`,
    );
  }
};

export const getOperationResponseV4 = (
  operationId: string,
  catalogFlag?: boolean,
): Promise<AxiosResponse<OperationResponseV4>> => {
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    return catalog.roles.getOperationV4(operationId, catalogConfig);
  } else {
    return granularAxiosInstance.get(
      `/api/roles-svc/v4/operations/${operationId}`,
    );
  }
};

const addOperation = (
  operationName: string,
  creatorRole: CreatorRole,
  catalogFlag?: boolean,
): Promise<Operation> => {
  let response;
  if (catalogFlag) {
    response = catalog.roles.addOperation(
      {
        name: operationName,
        creator_role: creatorRole,
      },
      { user_perms: true },
      { getAuthToken },
    );
  } else {
    response = granularAxiosInstance.post<
      DataForCreateOperationNetworkRequest,
      AxiosResponse<OperationResponse>
    >("/api/roles-svc/v2/operation?user_perms=true", {
      name: operationName,
      creator_role: creatorRole,
    });
  }
  return response.then((r) =>
    refinedOperation_NOT_MEMOIZED({
      ...r.data,
      // https://gitlab.encirca.auto.pioneer.com/core-accessibility/roles-svc/-/blob/master/roles_svc/api/v2/endpoint_scemas/op_setting_schemas.py#L9
      settings: {
        autogen_fields: true,
        allow_corteva_access: false,
      },
    }),
  );
};

const updateOperation = (
  operation: Operation,
  catalogFlag?: boolean,
): Promise<Operation> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operation.id,
    };
    response = Promise.all([
      catalog.roles.updateOperation(
        {
          id: operation.id,
          name: operation.name,
        },
        { user_perms: true },
        catalogConfig,
      ),
      catalog.roles.updateOperationSettings(
        {
          operation_id: operation.id,
          autogen_fields: operation.settings?.autogen_fields,
          allow_corteva_access: operation.settings?.allow_corteva_access,
        },
        catalogConfig,
      ),
    ]);
  } else {
    response = Promise.all([
      granularAxiosInstance.put<
        DataForUpdateOperationNetworkRequest,
        AxiosResponse<UpdateOperationNetworkResponse>
      >(`/api/roles-svc/v2/operation/${operation.id}?user_perms=true`, {
        id: operation.id,
        name: operation.name,
      }),
      granularAxiosInstance.put<
        DataForUpdateOperationSettingsNetworkRequest,
        AxiosResponse<UpdateOperationSettingsNetworkResponse>
      >(`/api/roles-svc/v2/operation/${operation.id}/setting`, {
        operation_id: operation.id,
        autogen_fields: operation.settings?.autogen_fields,
        allow_corteva_access: operation.settings?.allow_corteva_access,
      }),
    ]);
  }

  return response.then(([o, os]) =>
    refinedOperation_NOT_MEMOIZED({
      ...o.data,
      settings: {
        autogen_fields: os.data?.autogen_fields,
        allow_corteva_access: os.data?.allow_corteva_access,
      },
    }),
  );
};

const removeOperation = async (
  operationId: string,
  catalogFlag?: boolean,
): Promise<null> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    response = await catalog.roles.deleteOperation(operationId, catalogConfig);
  } else {
    response = await granularAxiosInstance.delete(
      `/api/roles-svc/v2/operation/${operationId}`,
    );
  }
  return response.data;
};

/**
 * Member Management
 */

export const getConfirmedMembers = async (
  {
    authToken,
    userId,
    operationId,
    isAarEnabled,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    isAarEnabled: boolean;
  },
  catalogFlag?: boolean,
): Promise<MembersResponse> => {
  try {
    const userPromises = [];
    if (catalogFlag) {
      const catalogConfig = {
        // IMPORTANT: We are not passing the authToken as part of the queryKey
        // because we don't want to invalidate our data on auth token change.
        // The auth token can and will change throughout the lifetime of the app,
        // but that should not invalidate our data.
        getAuthToken,
        getOperationId: () => operationId,
      };
      userPromises.push(
        catalog.roles.getUsers(
          operationId,
          { limit: OPERATIONS_PAGE_SIZE },
          catalogConfig,
        ),
      );
    } else {
      userPromises.push(
        axiosInstance.get<MembersNetworkResponse>(
          `/api/roles-svc/v2/operation/${operationId}/user?limit=${OPERATIONS_PAGE_SIZE}`,
          {
            headers: computeHeaders({
              authToken,
              userId,
              operationId,
            }),
          },
        ),
      );
    }

    if (isAarEnabled) {
      if (catalogFlag) {
        const catalogConfig = {
          // IMPORTANT: We are not passing the authToken as part of the queryKey
          // because we don't want to invalidate our data on auth token change.
          // The auth token can and will change throughout the lifetime of the app,
          // but that should not invalidate our data.
          getAuthToken,
          getOperationId: () => operationId,
        };
        userPromises.push(
          catalog.roles.getCortevaUsers(operationId, catalogConfig),
        );
      } else {
        userPromises.push(
          axiosInstance.get<MembersNetworkResponse>(
            `/api/roles-svc/v2/operation/${operationId}/corteva_user`,
            {
              headers: computeHeaders({
                authToken,
                userId,
                operationId,
              }),
            },
          ),
        );
      }
    }

    const [userResponse, cortevaUserResponse] = await Promise.all(userPromises);

    let cortevaUsers: MemberResponse[] = [];

    const responseETags: string[] = [];

    const totalUsers = userResponse.data._metadata.count;
    let loadedMembers: MemberResponse[] = [];
    const promises = [];

    loadedMembers = loadedMembers.concat(userResponse.data.users);
    responseETags.push(`granular-0-${userResponse.headers.etag}`);

    if (isAarEnabled) {
      cortevaUsers = cortevaUserResponse.data.users;
      responseETags.push(`corteva-0-${cortevaUserResponse.headers.etag}`);
    }

    // If there are more objects, create concurrent requests to get the remaining operations.
    if (userResponse.data._metadata.count <= OPERATIONS_PAGE_SIZE) {
      return {
        members: [...loadedMembers, ...cortevaUsers],
        hash: responseETags.join(" "),
      };
    }

    for (
      let offset = OPERATIONS_PAGE_SIZE;
      offset < totalUsers;
      offset += OPERATIONS_PAGE_SIZE
    ) {
      if (catalogFlag) {
        const catalogConfig = {
          // IMPORTANT: We are not passing the authToken as part of the queryKey
          // because we don't want to invalidate our data on auth token change.
          // The auth token can and will change throughout the lifetime of the app,
          // but that should not invalidate our data.
          getAuthToken,
          getOperationId: () => operationId,
        };
        promises.push(
          catalog.roles.getUsers(
            operationId,
            { limit: OPERATIONS_PAGE_SIZE, offset },
            catalogConfig,
          ),
        );
      } else {
        promises.push(
          axiosInstance.get<MembersNetworkResponse>(
            `/api/roles-svc/v2/operation/${operationId}/user?limit=${OPERATIONS_PAGE_SIZE}&offset=${offset}`,
            {
              headers: computeHeaders({
                authToken,
                userId,
                operationId,
              }),
            },
          ),
        );
      }
    }
    const results = await Promise.all(promises);
    results.forEach((response, index) => {
      loadedMembers = loadedMembers.concat(response.data.users);
      responseETags.push(
        `granular-${(index + 1) * OPERATIONS_PAGE_SIZE}-${
          response.headers.etag
        }`,
      );
    });

    return {
      members: loadedMembers.concat(cortevaUsers),

      /**
       * FIXME:
       *
       * @deprecated Unfortunately, the backend services don't provide the etag value correctly or consistently so we can't use this right now.
       * https://granular.atlassian.net/browse/APLO-2234
       */
      hash: responseETags.join(" "),
    };
  } catch (_e) {
    const e = ensureError(_e);
    if (getIsAxiosError(e) && e.response && e.response.status === 403) {
      return { members: [], hash: "--not-allowed--" };
    }
    throw e;
  }
};

const removeMemberFromOperation = async (
  {
    authToken,
    userId,
    operationId,
    memberId,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    memberId: string;
  },
  catalogFlag?: boolean,
): Promise<void> => {
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    await catalog.roles.deleteUser(operationId, memberId, catalogConfig);
  } else {
    await axiosInstance.delete(
      `/api/roles-svc/v2/operation/${operationId}/user/${memberId}`,
      {
        headers: computeHeaders({
          authToken,
          userId,
          operationId,
        }),
      },
    );
  }
};

const updateMemberPermissions = async (
  {
    authToken,
    userId,
    operationId,
    member,
    permissions,
  }: {
    authToken: string;
    userId: string;
    operationId: string;
    member: Member;
    permissions: UpdatePermissionsRequest;
  },
  catalogFlag?: boolean,
): Promise<void> => {
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getOperationId: () => operationId,
    };
    await catalog.roles.updateUsers(
      operationId,
      [
        {
          granular_id: member.granularId,
          user_role: {
            base_role:
              permissions.role ?? getRoleFromPermissions(member.permissions),
            financial:
              permissions.financial ?? member.permissions.canViewFinancials,
            nitrogen:
              permissions.nitrogen ?? member.permissions.canAccessNitrogen,
          },
        },
      ],
      catalogConfig,
    );
  } else {
    await axiosInstance.request({
      method: "PUT",
      url: `/api/roles-svc/v2/operation/${operationId}/user`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
      }),
      data: [
        {
          granular_id: member.granularId,
          user_role: {
            base_role:
              permissions.role ?? getRoleFromPermissions(member.permissions),
            financial:
              permissions.financial ?? member.permissions.canViewFinancials,
            nitrogen:
              permissions.nitrogen ?? member.permissions.canAccessNitrogen,
          },
        },
      ],
    });
  }
};

/**
 * Search upstream for the given operation name fragment. See the Roles Service
 * documentation for the details of the search implementation (TL;DR it's a
 * basic fuzzy search for now with FTS ("Full Text Search") that is scheduled
 * for later ✨)
 *
 * NOTE: There's absolutely no need to expose this via hook. Please stop it.
 * Resist the impulse. You can do it 🌸
 */
const searchForOperations = async (
  searchTerm: string,
  catalogFlag?: boolean,
): Promise<Operation[]> => {
  let response;
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
    };
    response = await catalog.roles.getOperations(
      {
        search_term: searchTerm,
        settings: true,
        user_perms: true,
        limit: OPERATIONS_PAGE_SIZE,
      },
      catalogConfig,
    );
  } else {
    const searchTermEncoded = encodeURIComponent(searchTerm);
    response = await granularAxiosInstance.get<OperationsNetworkResponse>(
      `/api/roles-svc/v2/operation?search_term=${searchTermEncoded}&settings=true&user_perms=true&limit=${OPERATIONS_PAGE_SIZE}`,
    );
  }

  return refinedListOfOperations_NOT_MEMOIZED(response.data.operations);
};

export default {
  CURRENT_OPERATION_KEY_IN_LOCAL_STORAGE,
  addOperation,
  getConfirmedMembers,
  getListOfOperationsResponse,
  getListOfOperations,
  removeMemberFromOperation,
  removeOperation,
  updateMemberPermissions,
  updateOperation,
  getOperationResponse,
  getOperationResponseV4,
  searchForOperations,
};
